import cn from 'classnames';
import { toPrettyNumber } from '@src/utils/performance-utils';
import { CITATION_SUMMARY_PRODUCTS_LIMIT } from '../../alexa-visibility-constants';
import {
  PositionedProduct,
  QueryCitationStats,
  TargetCitationSummary
} from '../../alexa-visibility-citation-utils';
import { isSameBrand } from '../../alexa-visibility-utils';
import { CitationMedal } from './CitationMedal';

export const formatCountOfTotal = (count: number, total: number): string =>
  `${toPrettyNumber(count)}/${toPrettyNumber(total)}`;

export const formatPercentOfTotal = (count: number, total: number): string =>
  `(${total > 0 ? ((count / total) * 100).toFixed(0) : 0}%)`;

interface SummaryRowProps {
  label: string;
  count: number;
  total: number;
  className?: string;
  place?: number;
}

const SummaryRow = ({ label, count, total, className, place }: SummaryRowProps) => (
  <div className={cn('aars-cc-summary-row', className)}>
    <span className='aars-cc-summary-label'>
      {place !== undefined && <CitationMedal place={place} />}
      {label}
    </span>
    <span className='aars-cc-summary-value'>
      <strong>{formatCountOfTotal(count, total)}</strong> {formatPercentOfTotal(count, total)}
    </span>
  </div>
);

// How often the selected brand was cited, and where it ranked. Used on the page and in each
// query card (with the card's own results).
export const TargetCitationRows = ({ summary }: { summary: TargetCitationSummary }) => (
  <div className='aars-cc-summary-rows'>
    <SummaryRow label='Cited' count={summary.cited} total={summary.total} />
    <SummaryRow
      label='Ranked #1'
      place={1}
      count={summary.rank1}
      total={summary.total}
      className='is-rank-1'
    />
    <SummaryRow
      label='Ranked #2'
      count={summary.rank2}
      total={summary.total}
      className='is-rank-2'
    />
    <SummaryRow label='Ranked #3' count={summary.rank3} total={summary.total} />
    <SummaryRow
      label='Not ranked'
      count={summary.notRanked}
      total={summary.total}
      className='is-not-ranked'
    />
  </div>
);

interface ProductRowsProps {
  products: PositionedProduct[];
  total: number;
  targetBrand: string;
}

// The most recommended products, each with how often it took positions 1, 2 and 3. The selected
// brand's products are highlighted.
export const ProductPositionRows = ({ products, total, targetBrand }: ProductRowsProps) => (
  <div className='aars-cc-product-rows'>
    {products.map((product, index) => (
      <div
        key={product.label}
        className={cn('aars-cc-product-row', {
          'is-target': product.brand !== undefined && isSameBrand(product.brand, targetBrand)
        })}
      >
        <div className='aars-cc-product-main'>
          <span className='aars-cc-product-name'>
            <CitationMedal place={index + 1} />
            {product.label}
          </span>
          <span className='aars-cc-summary-value'>
            <strong>{formatCountOfTotal(product.count, total)}</strong>{' '}
            {formatPercentOfTotal(product.count, total)}
          </span>
        </div>
        <div className='aars-cc-product-positions'>
          {product.positions.map((count, position) => `#${position + 1}: ${count}`).join(' · ')}
        </div>
      </div>
    ))}
  </div>
);

interface SummaryCardsProps {
  targetBrand: string;
  scopeLabel: string;
  summary: TargetCitationSummary;
  products: PositionedProduct[];
  // One query is selected: the third card shows its top products instead of the top queries.
  topQueries: QueryCitationStats[];
  isSingleQuery: boolean;
}

const MedalList = ({
  items
}: {
  items: { key: string; title: string; detail: string; isTarget?: boolean }[];
}) => (
  <div className='aars-cc-medal-rows'>
    {items.map((item, index) => (
      <div key={item.key} className={cn('aars-cc-medal-row', { 'is-target': item.isTarget })}>
        <CitationMedal place={index + 1} />
        <div className='aars-cc-medal-text'>
          <div className='aars-cc-medal-title'>{item.title}</div>
          <div className='aars-cc-medal-detail'>{item.detail}</div>
        </div>
      </div>
    ))}
  </div>
);

export const CitationSummaryCards = ({
  targetBrand,
  scopeLabel,
  summary,
  products,
  topQueries,
  isSingleQuery
}: SummaryCardsProps) => (
  <div className='aars-cc-summary-grid'>
    <div className='aars-cc-card'>
      <div className='aars-cc-card-title'>
        {targetBrand} citation summary <span className='aars-cc-card-note'>{scopeLabel}</span>
      </div>
      <TargetCitationRows summary={summary} />
    </div>
    <div className='aars-cc-card'>
      <div className='aars-cc-card-title'>
        Brand citation summary <span className='aars-cc-card-note'>top 3 positions</span>
      </div>
      <ProductPositionRows
        products={products.slice(0, CITATION_SUMMARY_PRODUCTS_LIMIT)}
        total={summary.total}
        targetBrand={targetBrand}
      />
    </div>
    <div className='aars-cc-card'>
      {isSingleQuery ? (
        <>
          <div className='aars-cc-card-title'>
            Top 3 products <span className='aars-cc-card-note'>for this query</span>
          </div>
          <MedalList
            items={products.slice(0, 3).map(product => ({
              key: product.label,
              title: product.label,
              detail: `${formatCountOfTotal(product.count, summary.total)} ${formatPercentOfTotal(
                product.count,
                summary.total
              )}`,
              isTarget: product.brand !== undefined && isSameBrand(product.brand, targetBrand)
            }))}
          />
        </>
      ) : (
        <>
          <div className='aars-cc-card-title'>
            Top 3 queries for {targetBrand}{' '}
            <span className='aars-cc-card-note'>by visibility score</span>
          </div>
          <MedalList
            items={topQueries.map(stat => ({
              key: stat.query,
              title: stat.query,
              detail: `${stat.visibilityScore.toFixed(0)}% visibility · ${stat.runs} ${
                stat.runs === 1 ? 'run' : 'runs'
              }`
            }))}
          />
        </>
      )}
    </div>
  </div>
);
